import debounce from 'lodash/debounce';

type VoidFn = () => void;
type PostMessage<T = Record<string, unknown>> = { postMessage: (param: T) => void };

interface StartReservationParams {
  edition: string
}

interface ShareParams {
  url: string;
  title: string;
  description: string;
  image?: string;
}

declare global {
  interface Window {
    android: {
      /** @deprecated */
      rotateToLandscape: VoidFn;
      /** @deprecated */
      rotateToPortrait: VoidFn;
      /** @deprecated */
      startMyDesign: VoidFn;
      /** @deprecated */
      startReservation: (params: StartReservationParams) => void;
      /** @deprecated */
      startViewConfigurator: VoidFn
      closeViewConfigurator: VoidFn;
      login: VoidFn;
      openVehicleCollection: VoidFn;
      shareUrl: (url: string, title: string, description: string, image: string) => void;
    };
    webkit: {
      messageHandlers: {
        /** @deprecated */
        rotateToLandscape: PostMessage;
        /** @deprecated */
        rotateToPortrait: PostMessage;
        /** @deprecated */
        startMyDesign: PostMessage;
        /** @deprecated */
        startReservation: PostMessage<StartReservationParams>;
        /** @deprecated */
        startViewConfigurator: PostMessage;
        closeViewConfigurator: PostMessage;
        login: PostMessage;
        openVehicleCollection: PostMessage;
        shareUrl: PostMessage;
      }
    };
  }
}


function isAndroid() {
  return /Android/.test(navigator.userAgent);
}

function isiOS() {
  return (
    /iPad|iPhone|iPod/.test(navigator.platform) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

const DEBOUNCE_WAIT_TIME = 3000;

function debounceWrapper<Func extends (...args) => Res, Res>(fn){
  return debounce<Func>(fn, DEBOUNCE_WAIT_TIME, { leading: true, trailing: false });
}

// Launch login
const loginHandler = debounceWrapper(() => {
  if (isAndroid()) {
    window.android.login();
  } else {
    window.webkit.messageHandlers.login.postMessage({});
  }
});

// Launch viewMyDesign
const myDesignHandler = debounceWrapper(() => {
  if (isAndroid()) {
    window.android.startMyDesign();
  } else {
    window.webkit.messageHandlers.startMyDesign.postMessage({});
  }
});

// Launch reservation
const reservationHandler = debounceWrapper(({ edition }) => {
  if (isAndroid()) {
    window.android.startReservation(edition);
  } else {
    window.webkit.messageHandlers.startReservation.postMessage({ edition });
  }
});

// Launch configurator
const configuratorHandler = debounceWrapper(() => {
  if (isAndroid()) {
    window.android.startViewConfigurator();
  } else {
    window.webkit.messageHandlers.startViewConfigurator.postMessage({});
  }
});

// Close configurator
const closeConfiguratorHandler = debounceWrapper(() => {
  if (isAndroid()) {
    window.android.closeViewConfigurator();
  } else {
    window.webkit.messageHandlers.closeViewConfigurator.postMessage({});
  }
});

const rotateToLandscape = debounceWrapper(() => {
  if (isAndroid()) {
    window.android.rotateToLandscape();
  } else {
    window.webkit.messageHandlers.rotateToLandscape.postMessage({});
  }
});

const rotateToPortrait = debounceWrapper(() => {
  if (isAndroid()) {
    window.android.rotateToPortrait();
  } else {
    window.webkit.messageHandlers.rotateToPortrait.postMessage({});
  }
});

// Close the webview, open the vehicle collection
const openVehicleCollectionHandler = debounceWrapper(() => {
  if (isAndroid()) {
    window.android.openVehicleCollection();
  } else {
    window.webkit.messageHandlers.openVehicleCollection.postMessage({});
  }
});

// Share handler for native app sharing
const shareHandler = debounceWrapper((params: ShareParams) => {
  const { url, title, description, image } = params;
  if (isAndroid()) {
    window.android.shareUrl(url, title, description, image || '');
  } else {
    window.webkit.messageHandlers.shareUrl.postMessage({ url, title, description, image });
  }
});

type GetHandlerByKeyTypes =
  | 'Configurator'
  | 'StartReservation'
  | 'CloseConfigurator'
  | 'Login'
  | 'ViewMyDesign'
  | 'RotateToLandscape'
  | 'RotateToPortrait'
  | 'OpenVehicleCollection'
  | 'Share';

type Options = {
  edition?: string;
  url?: string;
  title?: string;
  description?: string;
  image?: string;
}

const getHandlerByKey = (
  key: GetHandlerByKeyTypes,
  options?: Options
) => {
  if (key === 'Configurator') {
    return () => configuratorHandler();
  }
  if (key === 'StartReservation') {
    return () => reservationHandler({ edition: options?.edition });
  }
  if (key === 'CloseConfigurator') {
    return () => closeConfiguratorHandler();
  }
  if (key === 'Login') {
    return () => loginHandler();
  }
  if (key === 'ViewMyDesign') {
    return () => myDesignHandler();
  }
  if (key === 'RotateToLandscape') {
    return () => rotateToLandscape();
  }
  if (key === 'RotateToPortrait') {
    return () => rotateToPortrait();
  }
  if (key === 'OpenVehicleCollection') {
    return () => openVehicleCollectionHandler();
  }
  if (key === 'Share') {
    return () => shareHandler({
      url: options?.url || '',
      title: options?.title || '',
      description: options?.description || '',
      image: options?.image || ''
    });
  }

  throw new Error(`App handler not found for ${key}`);
};

export default {
  getHandlerByKey,
  isiOS,
  isAndroid,
  isMobile
};